import React from 'react';
import { AttributeEditor, FormField, Input, StatusIndicator } from '@amzn/awsui-components-console';
import { DDF_REGIONS } from '../../lib/constants';
import { fetchSubscribedGroups } from './apis';

// Delegated roles are named `adf-iam-auth-<team>`.
export const ROLE_NAME_PREFIX = 'adf-iam-auth-';
export const STATUS_ACTIVE = 'ACTIVE';
export const STATUS_INACTIVE = 'INACTIVE';

// The backend's rules (DS2DeviceFarmIAMManagementLambda constants.py), checked here only to
// fail fast. A team becomes the role name, and 50 characters keeps it within IAM's limit.
export const TEAM_NAME_RE = /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$/;
// An IAM role or user ARN. An account root principal is refused: it would let the set of
// callers grow without anyone here seeing it.
export const CALLER_ARN_RE = /^arn:aws(?:-[a-z-]+)?:iam::\d{12}:(?:role|user)\/.+$/;

/**
 * The status vending enforces. Items onboarded before `status` existed carry none and vend.
 * @param {object} role - a DDFCallerRegistry item
 */
export function roleStatus(role) {
    return (role && role.status) || STATUS_ACTIVE;
}

/**
 * Whether an offboard started and stopped part-way. The role is INACTIVE, and the owner may
 * retry the offboard to finish removing it.
 * @param {object} role - a DDFCallerRegistry item
 */
export function isOffboardInterrupted(role) {
    return !!(role && role.offboardStartedAt);
}

/**
 * The role with an explicit status, so filtering and sorting see ACTIVE on legacy items too.
 * @param {object} role - a DDFCallerRegistry item
 */
export function withStatus(role) {
    return { ...role, status: roleStatus(role) };
}

/**
 * Status cell: ACTIVE, INACTIVE, or an interrupted offboard that needs a retry.
 * @param {object} role - a DDFCallerRegistry item
 */
export function renderStatus(role) {
    if (isOffboardInterrupted(role)) {
        return <StatusIndicator type="error">Offboarding failed, retry</StatusIndicator>;
    }
    if (roleStatus(role) === STATUS_INACTIVE) {
        return <StatusIndicator type="stopped">{STATUS_INACTIVE}</StatusIndicator>;
    }
    return <StatusIndicator type="success">{STATUS_ACTIVE}</StatusIndicator>;
}

/**
 * @param {string} team - team name entered on onboard
 * @returns {string} the validation error, or '' when the team is usable
 */
export function validateTeam(team) {
    if (!TEAM_NAME_RE.test(team || '')) {
        return (
            `The team becomes the IAM role name ${ROLE_NAME_PREFIX}<team>, so it must be 2-50 lowercase ` +
            'letters, digits or dashes, with no leading or trailing dash.'
        );
    }
    return '';
}

/**
 * Validate every trusted caller ARN.
 * @param {string[]} arns - caller ARNs as entered
 * @returns {string[]} one error per ARN, '' where the ARN is valid
 */
export function validateCallerArns(arns) {
    return arns.map((arn, index) => {
        const value = (arn || '').trim();
        if (!value) {
            return 'Enter an IAM role or user ARN, or remove this row.';
        }
        if (/:root$/.test(value)) {
            return 'An account root principal is not accepted. Name a specific IAM role or user.';
        }
        if (!CALLER_ARN_RE.test(value)) {
            return 'Expected arn:aws:iam::<12-digit account>:role/<name> or user/<name>.';
        }
        if (arns.findIndex((other) => (other || '').trim() === value) !== index) {
            return 'This ARN is already listed.';
        }
        return '';
    });
}

/**
 * The backend's message for a rejected request, if it sent one.
 * @param {*} err - error thrown by the API wrapper
 * @returns {string|undefined}
 */
export function apiErrorMessage(err) {
    const data = err && err.response && err.response.data;
    if (data && typeof data === 'object' && data.errorMessage) {
        return data.errorMessage;
    }
    return undefined;
}

/**
 * HTTP status of a rejected request.
 * @param {*} err - error thrown by the API wrapper
 * @returns {number|undefined}
 */
export function apiErrorStatus(err) {
    return err && err.response ? err.response.status : undefined;
}

const groupOptionValue = (region, groupId) => `${region}_${groupId}`;

/**
 * Split a private group option value back into its region and group id. Region names have no
 * underscore, so the first one is the separator even if the group id contains more.
 * @param {string} value - `<region>_<group id>`
 */
export function parseGroupOptionValue(value) {
    const separator = value.indexOf('_');
    return { region: value.slice(0, separator), groupId: value.slice(separator + 1) };
}

/**
 * Private group options per region, for the Multiselect. The Common (root) group is never
 * offered: every identity gets it automatically and it is not managed here.
 * @param {function} include - which non-root groups to offer
 * @returns {Promise<Array>} option groups, one per region
 */
export async function loadPrivateGroupOptions(include = () => true) {
    return await Promise.all(
        DDF_REGIONS.map(async ({ regionName }) => {
            const response = await fetchSubscribedGroups(regionName);
            return {
                label: regionName,
                options: response['response']
                    .filter((group) => group.privilege !== 'root' && include(group))
                    .map((group) => ({
                        label: group.name,
                        value: groupOptionValue(regionName, group.id),
                        description: group.id,
                    })),
            };
        })
    );
}

/**
 * Preselect a role's current private groups. A group the role is in but the user cannot see is
 * still added (labelled by its id), so saving the form does not silently unsubscribe it.
 * @param {Array} groupOptions - option groups from loadPrivateGroupOptions
 * @param {Object<string, string[]>} subscribedGroups - the role's private group ids per region
 * @returns {{options: Array, selectedOptions: Array}}
 */
export function preselectSubscribedGroups(groupOptions, subscribedGroups) {
    const selectedOptions = [];
    const options = groupOptions.map((regionGroup) => {
        const subscribed = (subscribedGroups || {})[regionGroup.label] || [];
        const regionOptions = [...regionGroup.options];
        subscribed.forEach((groupId) => {
            let option = regionOptions.find((candidate) => candidate.description === groupId);
            if (!option) {
                option = { label: groupId, value: groupOptionValue(regionGroup.label, groupId), description: groupId };
                regionOptions.push(option);
            }
            selectedOptions.push(option);
        });
        return { ...regionGroup, options: regionOptions };
    });
    return { options, selectedOptions };
}

/**
 * The API's subscribedGroups map from the selected options.
 * @param {Array} selectedOptions - selected Multiselect options
 * @returns {Object<string, string[]>} private group ids per region
 */
export function toSubscribedGroups(selectedOptions) {
    const subscribedGroups = {};
    selectedOptions.forEach((option) => {
        const { region, groupId } = parseGroupOptionValue(option.value);
        subscribedGroups[region] = [...(subscribedGroups[region] || []), groupId];
    });
    return subscribedGroups;
}

/**
 * Trusted caller ARNs editor: one row per ARN. The last row cannot be removed, because a role
 * with no trusted caller cannot be used by anyone.
 * @param {object} props
 * @param {string[]} props.arns - caller ARNs as entered
 * @param {function} props.onChange - called with the new list of ARNs
 * @param {string[]} props.itemErrors - one validation error per ARN
 * @param {string} props.lastItemHint - shown when only one ARN is left
 */
export function CallerArnsEditor({ arns, onChange, itemErrors, lastItemHint }) {
    const setArn = (index, value) => onChange(arns.map((arn, i) => (i === index ? value : arn)));
    return (
        <FormField
            label="Trusted caller ARNs"
            description="IAM roles or users in your AWS account that may assume the Device Farm role to get a token."
        >
            <AttributeEditor
                items={arns.map((arn) => ({ arn }))}
                definition={[
                    {
                        label: 'IAM role or user ARN',
                        control: (item, index) => (
                            <Input
                                value={item.arn}
                                placeholder="arn:aws:iam::111122223333:role/my-pipeline-role"
                                ariaLabel={`Trusted caller ARN ${index + 1}`}
                                onChange={({ detail }) => setArn(index, detail.value)}
                            />
                        ),
                        errorText: (item, index) => itemErrors[index] || null,
                    },
                ]}
                onAddButtonClick={() => onChange([...arns, ''])}
                onRemoveButtonClick={({ detail }) => onChange(arns.filter((_, i) => i !== detail.itemIndex))}
                isItemRemovable={() => arns.length > 1}
                additionalInfo={arns.length === 1 ? lastItemHint : undefined}
                addButtonText="Add trusted caller"
                removeButtonText="Remove"
                empty="No trusted callers"
            />
        </FormField>
    );
}
